import { GameServerCode, GameServer } from '@/model/gameserver';
import { createGlobalState } from '@vueuse/core';
import { computed } from 'vue';
import { useRefOrLocalStorage } from '@/helpers/reforstorage';

// In localStorage a tab on ?server=pacman took over the hosts that another tab on the live server
// wrote, and connected to live under the pacman label. Each tab reads its conf on start anyway.
const gameServer = useRefOrLocalStorage('server-gameserver', new GameServer);

export const useGameServer = createGlobalState(() => {

    const code = computed(() => gameServer.value.code as GameServerCode);

    /**
     * Adds a host url to the list of possible hosts
     * @param url the websocket url of the host
     */
    function addHost(url: string) {
        gameServer.value.urls.push(url);
    }

    function getHosts(): readonly string[] {
        return gameServer.value.urls;
    }

    function clearHosts() {
        gameServer.value.urls.splice(0);
    }

    /**
     * Gets the host which should be tried for connecting
     * 
     * @return Host to which we should connect
     * @see GameServer.Host
     */
    function getHost() {
        if (gameServer.value.urls.length === 0) {
            console.error('no game server defined');
            return ""
        }
        if (gameServer.value.hostIndex >= gameServer.value.urls.length) {
            gameServer.value.hostIndex = 0;
        }
        return gameServer.value.urls[gameServer.value.hostIndex];
    }

    /**
     * Selects the next host to connect to
     * 
     * @return true is there is another host to connect to. False if we tried all
     *         hosts.
     */
    function nextHost() {
        gameServer.value.hostIndex++;

        if (gameServer.value.hostIndex >= gameServer.value.urls.length) {
            gameServer.value.hostIndex = 0;
        }

        gameServer.value.hostCount++;

        if (gameServer.value.hostCount >= gameServer.value.urls.length) {
            gameServer.value.hostCount = 0; // reset count
            console.warn('no more hosts available.');
            return false;
        }

        return true;
    }

    /**
     * Resets the host counter, so a call to nextHost() will start over. Must be
     * called whenever all hosts are tried, or when a succesful connection was made
     */
    function connectSuccesful() {
        gameServer.value.hostCount = 0;
    }

    function setApi(api: string) {
        gameServer.value.api = api;
    }

    function getApi() {
        return gameServer.value.api;
    }

    // equals(other: GameServer) {
    //     return other && this.id === other.id;
    // }

    function isNL() {
        return gameServer.value.code === GameServerCode.NL;
    }

    function isUK() {
        return gameServer.value.code === GameServerCode.UK;
    }

    function isAU() {
        return gameServer.value.code === GameServerCode.AU;
    }

    function setNL() {
        gameServer.value.code = GameServerCode.NL;
    }

    function setUK() {
        gameServer.value.code = GameServerCode.UK;
    }

    function setAU() {
        gameServer.value.code = GameServerCode.AU;
    }


    return {
        clearHosts, addHost, getHost, getHosts, nextHost, connectSuccesful,
        isAU, isNL, isUK,
        setAU, setNL, setUK,
        setApi, getApi, code
    }
});